// The Pulse feed's relative date label (gh-657, Screen 25).
//
// The mockup writes two shapes and only two: `Today · 08:12` for entries from
// the current day, and a short weekday name (`Mon`, `Fri`) for everything
// older. That is the whole field contract, so this renders those and invents
// nothing — no "Yesterday", no dates, no "3 days ago".
//
// Everything here is computed in the READER's local zone, deliberately. The
// wire value is a UTC instant; "today" is a question about the person looking
// at the screen, not about UTC. An org_admin in Sydney reading an event from
// 23:00 UTC must see it under the day they are actually living in.

/** The mockup's separator is U+00B7 MIDDLE DOT, not a hyphen or an em dash. */
const SEPARATOR = "\u00b7";

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * `occurredAt` is the entry's ISO-8601 UTC instant; `now` is injected so the
 * caller — and the tests — decide what "today" means rather than reaching for
 * the wall clock inside a render.
 *
 * An unparseable instant returns the empty string. The audit store cannot
 * produce one, but this label is decoration on a feed: a malformed row must
 * cost its own `<strong>` and nothing else, never an `Invalid Date` rendered
 * to an org_admin.
 */
export function formatPulseTimestamp(occurredAt: string, now: Date): string {
  const at = new Date(occurredAt);
  if (Number.isNaN(at.getTime())) return "";

  if (isSameLocalDay(at, now)) {
    // `hourCycle: "h23"` rather than `hour12: false`. They are not synonyms:
    // `hour12: false` selects the h24 cycle on some ICU builds, which renders
    // midnight as `24:12` instead of `00:12` — a real time nobody writes, on
    // the one hour of the day the difference exists. `h23` names the cycle the
    // mockup uses and leaves nothing to the build.
    const time = at.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    return `Today ${SEPARATOR} ${time}`;
  }

  return at.toLocaleDateString(undefined, { weekday: "short" });
}
